import React, { useEffect, useRef, useState } from "react";
import "./ProductDetail.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface MobileImageConfig {
  src: string;
  width?: number; // パーセンテージ (1-100)。未指定なら100%
}

interface ProductDetailProps {
  id: string;
  title: string;
  titleImg: string;
  subtitle: string;
  description: string;
  sampleLink: string;
  imagePosition: "left" | "right";
  themeColor: string;
  titleClass: string;
  pcImage: string;
  mobileImages?: (string | MobileImageConfig)[]; // 文字列またはオブジェクト
  showMoireLink: boolean;
}

const ProductDetail: React.FC<ProductDetailProps> = ({
  id,
  title,
  titleImg,
  subtitle,
  description,
  sampleLink,
  imagePosition,
  themeColor,
  titleClass,
  pcImage,
  mobileImages,
  showMoireLink,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // 初期状態を確実に表示
    if (imageRef.current && contentRef.current) {
      gsap.set([imageRef.current, contentRef.current], {
        opacity: 1,
        x: 0,
      });
    }

    // シンプルなフェードインアニメーション
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top 80%",
        toggleActions: "play none none none",
        once: true,
      },
    });

    if (imagePosition === "left") {
      tl.fromTo(
        imageRef.current,
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: "power2.out",
        },
        0,
      ).fromTo(
        contentRef.current,
        { opacity: 0, x: 50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: "power2.out",
        },
        0.2,
      );
    } else {
      tl.fromTo(
        contentRef.current,
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: "power2.out",
        },
        0,
      ).fromTo(
        imageRef.current,
        { opacity: 0, x: 50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: "power2.out",
        },
        0.2,
      );
    }
  }, [imagePosition]);

  return (
    <section
      className={`product-detail ${imagePosition === "right" ? "reverse" : ""} product-${id}`}
      id={id}
      ref={sectionRef}
      style={
        {
          "--theme-color": themeColor,
        } as React.CSSProperties
      }
    >
      <div className="detail-container">
        <div className="detail-image" ref={imageRef}>
          {isMobile && mobileImages ? (
            <div className="mobile-images">
              {mobileImages.map((img, index) => {
                const imgSrc = typeof img === "string" ? img : img.src;
                const imgWidth =
                  typeof img === "string" ? 100 : img.width || 100;
                return (
                  <div
                    key={index}
                    className="mobile-image-item"
                    style={{ width: `${imgWidth}%` }}
                  >
                    <img src={imgSrc} alt={`${title} - ${index + 1}`} />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="pc-image">
              <img src={pcImage} alt={title} />
            </div>
          )}
        </div>

        <div className="detail-content" ref={contentRef}>
          <h2 className={titleClass}>
            <img src={titleImg} alt={title} />
          </h2>
          <h3 className="detail-subtitle">{subtitle}</h3>
          <p className="detail-description">
            {description.split("\\n").map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < description.split("\\n").length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
          <a
            href={sampleLink}
            className="sample-button"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${title}のサンプルを見る`}
          >
            <span>サンプルはこちら</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 10H16M16 10L10 4M16 10L10 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
          {showMoireLink ? (
            <a
              href="https://www.kyodoprinting.co.jp/lp/moire/index.html"
              className="sample-button"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="モアレについて詳しく見る"
            >
              <span>モアレとは</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 10H16M16 10L10 4M16 10L10 16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default ProductDetail;
